import { registerApplication } from 'single-spa'

import { createIframeElement } from 'components/apps/fullscreen/microApp/createIframeElement'
import { setupIframeCommunication } from 'components/apps/fullscreen/microApp/setupIframeCommunication'
import { FullscreenAppCustomProps, MicroIframeAppConfig } from 'types/common/singleSpa'
import { isRegistered } from 'utils/singleSpa'

const tokenUpdaters = new Map<string, (jwt: string) => void>()

export const registerMicroAppInIframe = (microAppConfig: MicroIframeAppConfig) => {
  const {
    domElementId,
    stableId,
    bundleUrl,
    customProps,
    activeWhen,
    currentBaseUrl,
    libraryType,
    jwt,
    devhubAppVersionId,
  } = microAppConfig

  if (!isRegistered(stableId)) {
    let cleanupFn: (() => void) | null = null

    registerApplication({
      name: stableId,
      app: async () => ({
        bootstrap: () => Promise.resolve(),
        // NOTE: The `mount` function is a separate application in single-spa and does not inherit React from the parent.
        // Simply importing React is not enough — React must be explicitly initialized inside `mount` using `ReactDOM.createRoot().render(...)`.
        // Taking this into account and all its relevant implications, this code is written in framework agnostic vanilla way.
        mount: async () => {
          const container = document.getElementById(domElementId)
          if (!container) {
            console.error(`Container with ID '${domElementId}' not found`)
            return Promise.reject(new Error(`Container with ID '${domElementId}' not found`))
          }

          const iframe = createIframeElement({
            stableId,
            currentBaseUrl,
            bundleUrl,
            libraryType,
            container,
            devhubAppVersionId,
          })

          const { cleanup, updateAccessToken } = setupIframeCommunication(
            iframe,
            currentBaseUrl,
            customProps as FullscreenAppCustomProps,
          )
          tokenUpdaters.set(stableId, updateAccessToken)
          cleanupFn = cleanup
        },
        unmount: () => {
          const iframe = document.getElementById(stableId)
          if (iframe) {
            iframe.remove()
          }
          cleanupFn?.()
          cleanupFn = null
          return Promise.resolve()
        },
      }),
      activeWhen,
      customProps,
    })
  } else {
    const updateAccessToken = tokenUpdaters.get(stableId)
    updateAccessToken?.(jwt)
  }
}
